import { OTHER_SPECIALTY_ID } from '@/components/ui/select/clinician-type-select';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useMemo } from 'react';

import { useOnboardingContext } from '../context/OnboardingContext';

/**
 * Localized display name of the currently-selected onboarding specialty, in
 * the active display language.
 *
 * `formData.specialty` holds the canonical item (English `name`), so the
 * rendered label is re-derived by id from the specialties list, whose
 * `localizedName` follows the active language (and the
 * `release-scribe-be-specialty-translation` source switch) automatically.
 * We deliberately do NOT mutate `formData.specialty.name` — the
 * `/onboarding-v2-assets` lookup keys on the canonical name.
 *
 * Returns `undefined` when no specialty is selected. The free-text "Other"
 * option returns the clinician's typed value verbatim.
 *
 * Source of truth for the mic-test signature (NoteSignature), the
 * personalised mic-test heading (see MicTestStep), and the onboarding
 * preview surfaces (PreviewPanel, MicTestPreview, LearnBasicsFlowSteps).
 */
export function useLocalizedSpecialtyName(): string | undefined {
  const { formData } = useOnboardingContext();
  const { data: specialties } = useQuerySpecialties((d) => d.specialties);

  return useMemo<string | undefined>(() => {
    const specialty = formData.specialty;
    if (!specialty) return undefined;
    // "Other" is free text the clinician typed — never a translatable name.
    if (specialty.id === OTHER_SPECIALTY_ID) {
      return formData.otherSpecialty?.trim() || specialty.name;
    }
    return (
      specialties?.find((s) => s.id === specialty.id)?.localizedName ?? specialty.name
    );
  }, [formData.specialty, formData.otherSpecialty, specialties]);
}
